import React, { useState, useEffect } from 'react'
import { FaMagnifyingGlassPlus, FaPlus, FaMinus, FaArrowsRotate } from 'react-icons/fa6'
import { useIntl } from 'react-intl'

// Control bar shown on a single line right after the x-axis legend of a graph.
// - loupe: enlarges the graph (opens the popup); only shown when `onEnlarge` is
//   provided (i.e. on the inline graphs, not inside the already-enlarged popup).
// - reset: brings the graph back to its initial position/zoom. Greyed out while
//   the graph is at its default view, turns blue once it is zoomed or panned.
// - plus / minus: zoom in / out on the x-axis.
//
// `getChart` returns the chart.js instance (kept as a getter because the chart
// is created in componentDidMount, after the first render). `afterChange` lets
// a graph refresh its dynamic x-axis ticks: the plugin's onZoomComplete does
// not fire on programmatic chart.zoom(), so the density must be recomputed here.
const btnStyle = {
    background: '#fff',
    border: '1px solid #ccc',
    borderRadius: '50%',
    cursor: 'pointer',
    color: '#2896E5',
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
    padding: 0,
}

const resetIdleStyle = {
    ...btnStyle,
    color: '#9aa0a6',
    border: '1px solid #e0e0e0',
    cursor: 'default',
}

const GraphZoomControls = ({ getChart, afterChange, onEnlarge }) => {
    const intl = useIntl()
    const [zoomed, setZoomed] = useState(false)

    const readZoomed = (chart) => (chart && chart.isZoomedOrPanned ? chart.isZoomedOrPanned() : false)

    const withChart = (fn) => {
        const chart = getChart && getChart()
        if (!chart || typeof chart !== 'object') return
        fn(chart)
        if (afterChange) afterChange(chart)
        setZoomed(readZoomed(chart))
    }

    const zoomIn = () => withChart((chart) => { if (chart.zoom) chart.zoom(1.2) })
    const zoomOut = () => withChart((chart) => { if (chart.zoom) chart.zoom(0.8) })
    const reset = () => withChart((chart) => { if (chart.resetZoom) chart.resetZoom('none') })

    // Keep the reset button in sync with zoom/pan done through chart gestures
    // (wheel, drag-select, pinch, shift-pan, double-click reset), which all
    // happen outside React. The chart is built in the parent's componentDidMount
    // — after this child mounts — so retry until the canvas is available.
    useEffect(() => {
        let canvas = null
        let cancelled = false
        const recompute = () => setTimeout(() => {
            if (cancelled) return
            const chart = getChart && getChart()
            if (chart && typeof chart === 'object') setZoomed(readZoomed(chart))
        }, 60)

        const attach = (attempts) => {
            if (cancelled) return
            const chart = getChart && getChart()
            if (chart && typeof chart === 'object' && chart.canvas) {
                canvas = chart.canvas
                canvas.addEventListener('wheel', recompute, { passive: true })
                canvas.addEventListener('mouseup', recompute)
                canvas.addEventListener('touchend', recompute)
                canvas.addEventListener('dblclick', recompute)
                setZoomed(readZoomed(chart))
            } else if (attempts > 0) {
                setTimeout(() => attach(attempts - 1), 100)
            }
        }
        attach(30)

        return () => {
            cancelled = true
            if (canvas) {
                canvas.removeEventListener('wheel', recompute)
                canvas.removeEventListener('mouseup', recompute)
                canvas.removeEventListener('touchend', recompute)
                canvas.removeEventListener('dblclick', recompute)
            }
        }
    }, [])

    return (
        <div style={{ display: 'flex', flex: '0 0 auto', justifyContent: 'center', alignItems: 'center', gap: 8, padding: '4px 0 2px' }}>
            <button type="button" onClick={zoomOut} title={intl.formatMessage({ id: 'graph.zoomOut', defaultMessage: 'Zoom arrière' })} style={btnStyle}>
                <FaMinus size={13} />
            </button>
            <button type="button" onClick={zoomIn} title={intl.formatMessage({ id: 'graph.zoomIn', defaultMessage: 'Zoom avant' })} style={btnStyle}>
                <FaPlus size={13} />
            </button>
            <button
                type="button"
                onClick={reset}
                disabled={!zoomed}
                title={intl.formatMessage({ id: 'graph.resetZoom', defaultMessage: 'Réinitialiser le graphique' })}
                style={zoomed ? btnStyle : resetIdleStyle}
            >
                <FaArrowsRotate size={13} />
            </button>
            {onEnlarge && (
                <button type="button" onClick={onEnlarge} title={intl.formatMessage({ id: 'graph.enlarge', defaultMessage: 'Agrandir le graphique' })} style={btnStyle}>
                    <FaMagnifyingGlassPlus size={14} />
                </button>
            )}
        </div>
    )
}

export default GraphZoomControls
